import distance from '@turf/distance'
import { lineString, point } from '@turf/helpers'
import simplify from '@turf/simplify'
import { inBoundary, type PropertyBoundary } from '@cht/shared'
import type { GeoFix } from '../platform/geolocation.js'

export interface TrailRecorderOptions {
  maxAccuracyMeters: number
  minimumLengthMeters: number
  simplifyTolerance: number
  geofenceBufferMeters: number
  sampleIntervalMs?: number
  sampleDistanceMeters?: number
}
export interface TrailDraft {
  id: string
  propertyId: string
  authorId: string
  name: string
  description: string
  status: 'draft'
  segments: GeoJSON.Position[][]
  kept: number
  discarded: number
  distanceMeters: number
  createdAt: number
  updatedAt: number
  syncState: 'device' | 'syncing' | 'server'
  serverCreated: boolean
  trimmedTail: Array<{ segmentIndex: number; position: GeoJSON.Position }>
}
export interface TrailDraftStore { put(draft: TrailDraft): Promise<unknown> }

const segmentDistance = (positions: GeoJSON.Position[]): number => positions.slice(1).reduce((total, position, index) => total + distance(point(positions[index]!), point(position), { units: 'kilometers' }) * 1000, 0)

export class TrailRecorder {
  private draft: TrailDraft | undefined
  private paused = false
  private lastAcceptedFix: GeoFix | undefined

  constructor(private readonly store: TrailDraftStore, private readonly boundary: PropertyBoundary, private readonly options: TrailRecorderOptions) {}

  start(id: string, propertyId: string, authorId: string, now: number): TrailDraft {
    this.paused = false
    this.lastAcceptedFix = undefined
    this.draft = { id, propertyId, authorId, name: '', description: '', status: 'draft', segments: [[]], kept: 0, discarded: 0, distanceMeters: 0, createdAt: now, updatedAt: now, syncState: 'device', serverCreated: false, trimmedTail: [] }
    return this.snapshot()
  }

  restore(draft: TrailDraft): TrailDraft {
    this.paused = true; this.lastAcceptedFix = undefined
    this.draft = structuredClone(draft)
    return this.snapshot()
  }

  pause(): void { if (this.draft) this.paused = true }
  resume(): void { if (this.draft && this.paused) { this.paused = false; this.lastAcceptedFix = undefined; this.draft.segments.push([]) } }

  async reject(recordedAt: number): Promise<TrailDraft> {
    if (!this.draft) throw new Error('Trail recording has not started')
    this.draft.discarded += 1
    this.draft.updatedAt = recordedAt
    await this.store.put(this.snapshot())
    return this.snapshot()
  }

  async accept(fix: GeoFix): Promise<boolean> {
    if (!this.draft || this.paused) return false
    if (fix.accuracyMeters > this.options.maxAccuracyMeters || !inBoundary(fix, this.boundary, this.options.geofenceBufferMeters)) {
      this.draft.discarded += 1
      this.draft.updatedAt = fix.recordedAt
      await this.store.put(this.snapshot())
      return false
    }
    if (this.lastAcceptedFix) {
      const elapsed = fix.recordedAt - this.lastAcceptedFix.recordedAt
      const moved = distance(point([this.lastAcceptedFix.longitude, this.lastAcceptedFix.latitude]), point([fix.longitude, fix.latitude]), { units: 'kilometers' }) * 1000
      if (elapsed < (this.options.sampleIntervalMs ?? 0) && moved < (this.options.sampleDistanceMeters ?? 0)) return false
    }
    this.draft.segments.at(-1)!.push([fix.longitude, fix.latitude])
    this.draft.trimmedTail = []
    this.lastAcceptedFix = fix
    this.draft.kept += 1
    this.draft.distanceMeters = this.draft.segments.reduce((sum, segment) => sum + segmentDistance(segment), 0)
    this.draft.updatedAt = fix.recordedAt
    await this.store.put(this.snapshot())
    return true
  }

  snapshot(): TrailDraft {
    if (!this.draft) throw new Error('Trail recording has not started')
    return structuredClone(this.draft)
  }

  async trimLastPoint(now = Date.now()): Promise<TrailDraft> {
    if (!this.draft) throw new Error('Trail recording has not started')
    let segmentIndex = this.draft.segments.length - 1
    while (segmentIndex >= 0 && this.draft.segments[segmentIndex]!.length === 0) segmentIndex -= 1
    if (segmentIndex < 0) throw new Error('There are no trail points to trim')
    const position = this.draft.segments[segmentIndex]!.pop()!
    this.draft.trimmedTail.push({ segmentIndex, position })
    this.draft.kept = Math.max(0, this.draft.kept - 1)
    this.draft.distanceMeters = this.draft.segments.reduce((sum, segment) => sum + segmentDistance(segment), 0)
    this.draft.updatedAt = now; this.draft.syncState = 'device'
    await this.store.put(this.snapshot())
    return this.snapshot()
  }

  async undoTrim(now = Date.now()): Promise<TrailDraft> {
    if (!this.draft) throw new Error('Trail recording has not started')
    const trimmed = this.draft.trimmedTail.pop()
    if (!trimmed) return this.snapshot()
    this.draft.segments[trimmed.segmentIndex] ??= []
    this.draft.segments[trimmed.segmentIndex]!.push(trimmed.position)
    this.draft.kept += 1
    this.draft.distanceMeters = this.draft.segments.reduce((sum, segment) => sum + segmentDistance(segment), 0)
    this.draft.updatedAt = now; this.draft.syncState = 'device'
    await this.store.put(this.snapshot())
    return this.snapshot()
  }

  async markSync(name: string, description: string, syncState: TrailDraft['syncState'], serverCreated: boolean, now = Date.now()): Promise<TrailDraft> {
    if (!this.draft) throw new Error('Trail recording has not started')
    this.draft.name = name; this.draft.description = description
    this.draft.syncState = syncState; this.draft.serverCreated = serverCreated; this.draft.updatedAt = now
    await this.store.put(this.snapshot())
    return this.snapshot()
  }

  geometry(): GeoJSON.LineString | GeoJSON.MultiLineString {
    if (!this.draft) throw new Error('Trail recording has not started')
    const segments = this.draft.segments.filter((segment) => segment.length >= 2)
      .map((segment) => simplify(lineString(segment), { tolerance: this.options.simplifyTolerance }).geometry.coordinates)
    if (!segments.length) throw new Error('Keep at least two trail points before saving')
    return segments.length === 1 ? { type: 'LineString', coordinates: segments[0]! } : { type: 'MultiLineString', coordinates: segments }
  }

  publish(name: string): GeoJSON.LineString | GeoJSON.MultiLineString {
    if (!this.draft) throw new Error('Trail recording has not started')
    if (this.draft.distanceMeters < this.options.minimumLengthMeters) throw new Error(`Trail must be at least ${this.options.minimumLengthMeters} m`)
    if (!name.trim()) throw new Error('Trail name is required')
    return this.geometry()
  }
}
